/**
 * Conversation-list state. A framework-agnostic port of mobile
 * `ChatListProvider` (ChangeNotifier): the same instance feeds the list
 * screen and the nav badge, so both stay in sync like the mobile shared
 * provider. React subscribes via `useSyncExternalStore` (see WU3).
 *
 * REST + IndexedDB + clock/storage are injected so tests run memory-only,
 * exactly like the provider's nullable-DB path.
 */
import {
  fetchChatUnreadCount,
  fetchConversationById,
  fetchConversations,
  mapConversationsError,
  markConversationRead,
  type ChatErrors,
} from './chatApi'
import { en } from '@/lib/i18n/en'
import {
  clearChatDb,
  conversationFromRow,
  conversationToRow,
  getConversations as dbGetConversations,
  replaceConversations as dbReplaceConversations,
  upsertConversation as dbUpsertConversation,
} from './chatDb'
import type { Conversation } from './types'

/** Mobile `loadConversations` TTL: skip backend round-trips on rapid tab swaps. */
export const CHAT_CACHE_TTL_MS = 5 * 60_000

/** Mobile `_kOwnerKey`: cached rows are trusted only for the recorded owner. */
export const CHAT_OWNER_KEY = 'chat_list_provider_cache_owner_v1'

export interface ConversationStoreDeps {
  fetchConversations: typeof fetchConversations
  fetchConversationById: typeof fetchConversationById
  postMarkRead: typeof markConversationRead
  fetchUnreadCount: typeof fetchChatUnreadCount
  replaceConversations: typeof dbReplaceConversations
  readConversations: typeof dbGetConversations
  upsertConversation: typeof dbUpsertConversation
  clearDb: typeof clearChatDb
  storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>
  now: () => number
}

export interface ConversationSnapshot {
  conversations: Conversation[]
  isLoading: boolean
  error: string | null
  unreadCount: number
}

export interface InboundPreview {
  conversationId: string
  preview: string
  at: string
  incrementUnread: boolean
}

const EMPTY: ConversationSnapshot = { conversations: [], isLoading: false, error: null, unreadCount: 0 }

export function defaultConversationStoreDeps(): ConversationStoreDeps {
  return {
    fetchConversations,
    fetchConversationById,
    postMarkRead: markConversationRead,
    fetchUnreadCount: fetchChatUnreadCount,
    replaceConversations: dbReplaceConversations,
    readConversations: dbGetConversations,
    upsertConversation: dbUpsertConversation,
    clearDb: clearChatDb,
    storage: typeof localStorage !== 'undefined'
      ? localStorage
      : ({ getItem: () => null, setItem: () => {}, removeItem: () => {} } as Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>),
    now: () => Date.now(),
  }
}

export class ChatConversationStore {
  private state: ConversationSnapshot = { ...EMPTY, conversations: [] }
  private listeners = new Set<() => void>()
  private lastFetch: number | null = null
  private ownerUserId: string | null = null
  private detailLoads = new Map<string, Promise<Conversation | null>>()

  constructor(private readonly deps: ConversationStoreDeps = defaultConversationStoreDeps()) {}

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener)
    return () => {
      this.listeners.delete(listener)
    }
  }

  getSnapshot = (): ConversationSnapshot => this.state

  private setState(patch: Partial<ConversationSnapshot>): void {
    this.state = { ...this.state, ...patch }
    for (const listener of this.listeners) listener()
  }

  getConversation(id: string): Conversation | undefined {
    return this.state.conversations.find((c) => c.id === id)
  }

  /**
   * Mobile `hydrate`: show cached rows immediately on mount. With a user
   * id, rebind first so a previous account's rows never render. No-ops
   * (no notify) when the cache already holds the same list.
   */
  async hydrate(userId?: string): Promise<void> {
    try {
      if (userId != null && userId !== '') {
        await this.setOwner(userId)
      } else if (this.ownerUserId == null) {
        this.ownerUserId = this.deps.storage.getItem(CHAT_OWNER_KEY)
      }
      const rows = await this.deps.readConversations()
      if (rows.length === 0) return
      const parsed = rows.map(conversationFromRow).sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1))
      const current = this.state.conversations
      if (parsed.length === current.length && parsed.length > 0 && parsed[0].id === current[0]?.id) {
        return
      }
      this.setState({ conversations: parsed })
    } catch {
      // Best-effort like mobile: a broken cache never blocks the network fetch.
    }
  }

  /** Mobile `loadConversations` with the 5-minute TTL guard (`force` bypasses). */
  async loadConversations({ force = false }: { force?: boolean } = {}, errors: ChatErrors = en.chat): Promise<void> {
    if (this.state.isLoading) return
    if (!force && this.lastFetch != null && this.deps.now() - this.lastFetch < CHAT_CACHE_TTL_MS) {
      return
    }
    this.setState({ isLoading: true, error: null })
    try {
      const page = await this.deps.fetchConversations()
      // Merge, don't replace: keep locally-known conversations the
      // backend hasn't propagated yet, and prefer local snapshots ahead
      // of the backend (optimistic sends).
      const merged = new Map<string, Conversation>()
      for (const c of page.items) merged.set(c.id, c)
      for (const c of this.state.conversations) {
        const prior = merged.get(c.id)
        if (!prior) merged.set(c.id, c)
        else if (c.updatedAt > prior.updatedAt) merged.set(c.id, c)
      }
      const conversations = [...merged.values()].sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1))
      this.setState({ conversations, unreadCount: page.totalUnread, isLoading: false })
      this.lastFetch = this.deps.now()
      await this.persist()
    } catch {
      this.setState({ error: mapConversationsError(null, errors), isLoading: false })
    }
  }

  /** Mobile `loadUnreadCount`: cheap badge refresh, silent on failure. */
  async loadUnreadCount(): Promise<void> {
    try {
      this.setState({ unreadCount: await this.deps.fetchUnreadCount() })
    } catch {
      // The badge keeps its last known value.
    }
  }

  /**
   * Mobile `ensureConversation`: the cached row, else the detail endpoint
   * (deep links, pushes). Concurrent callers share one inflight request.
   */
  ensureConversation(id: string, errors: ChatErrors = en.chat): Promise<Conversation | null> {
    const cached = this.getConversation(id)
    if (cached) return Promise.resolve(cached)
    let inflight = this.detailLoads.get(id)
    if (!inflight) {
      inflight = (async () => {
        try {
          const model = await this.deps.fetchConversationById(id)
          this.addOrUpdateConversation(model)
          return model
        } catch {
          this.setState({ error: mapConversationsError(null, errors) })
          return null
        } finally {
          this.detailLoads.delete(id)
        }
      })()
      this.detailLoads.set(id, inflight)
    }
    return inflight
  }

  /** Mobile `markAsRead`: optimistic zero with revert on failure. */
  async markAsRead(conversationId: string): Promise<void> {
    const index = this.state.conversations.findIndex((c) => c.id === conversationId)
    if (index === -1) return
    const previous = this.state.conversations[index]
    if (previous.unreadCount === 0) return
    const conversations = [...this.state.conversations]
    conversations[index] = { ...previous, unreadCount: 0 }
    const unreadCount = Math.max(0, this.state.unreadCount - previous.unreadCount)
    this.setState({ conversations, unreadCount })
    try {
      await this.deps.postMarkRead(conversationId)
    } catch {
      const rolledBack = [...this.state.conversations]
      const at = rolledBack.findIndex((c) => c.id === conversationId)
      if (at !== -1) rolledBack[at] = previous
      this.setState({ conversations: rolledBack, unreadCount: this.state.unreadCount + previous.unreadCount })
    }
  }

  /**
   * Mobile `applyMessage`: fold an inbound message into the list preview.
   * Unknown conversations resolve through the detail endpoint so a first
   * message from a brand-new chat still lands (false when unresolvable).
   */
  async applyInbound(inbound: InboundPreview): Promise<boolean> {
    if (!this.getConversation(inbound.conversationId) && (await this.ensureConversation(inbound.conversationId)) == null) {
      return false
    }
    const index = this.state.conversations.findIndex((c) => c.id === inbound.conversationId)
    const previous = this.state.conversations[index]
    const unread = previous.unreadCount + (inbound.incrementUnread ? 1 : 0)
    const updated = {
      ...previous,
      lastMessage: inbound.preview,
      lastMessageAt: inbound.at,
      updatedAt: inbound.at,
      unreadCount: unread,
    }
    const conversations = [...this.state.conversations]
    conversations[index] = updated
    conversations.sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1))
    this.setState({
      conversations,
      unreadCount: inbound.incrementUnread ? this.state.unreadCount + 1 : this.state.unreadCount,
    })
    await this.deps.upsertConversation(conversationToRow(updated)).catch(() => {})
    return true
  }

  /** Mobile `clearUnreadLocally`: the open room already shows everything. */
  clearUnreadLocally(conversationId: string): void {
    const index = this.state.conversations.findIndex((c) => c.id === conversationId)
    if (index === -1) return
    const count = this.state.conversations[index].unreadCount
    if (count === 0) return
    const conversations = [...this.state.conversations]
    conversations[index] = { ...conversations[index], unreadCount: 0 }
    this.setState({ conversations, unreadCount: Math.max(0, this.state.unreadCount - count) })
    void this.persist().catch(() => {})
  }

  /** Mobile `addOrUpdateConversation`: newest-first, persisted for restarts. */
  addOrUpdateConversation(conversation: Conversation): void {
    const index = this.state.conversations.findIndex((c) => c.id === conversation.id)
    const conversations = [...this.state.conversations]
    if (index !== -1) conversations[index] = conversation
    else conversations.unshift(conversation)
    conversations.sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1))
    this.setState({ conversations })
    void this.persist().catch(() => {})
  }

  /**
   * Mobile `setOwner`: rebinding to another user wipes the previous
   * account's memory + disk rows before anything new loads.
   */
  async setOwner(userId: string): Promise<void> {
    if (this.ownerUserId === userId) return
    try {
      const stored = this.deps.storage.getItem(CHAT_OWNER_KEY)
      if (stored != null && stored !== userId) {
        this.state = { ...EMPTY, conversations: [] }
        this.lastFetch = null
        await this.deps.clearDb()
      }
      this.ownerUserId = userId
      this.deps.storage.setItem(CHAT_OWNER_KEY, userId)
      this.setState({ conversations: [...this.state.conversations] })
    } catch {
      // Never block on storage failures.
    }
  }

  /** Mobile `wipeForLogout`: memory, disk rows and owner id all go. */
  async wipeForLogout(): Promise<void> {
    this.state = { ...EMPTY, conversations: [] }
    this.lastFetch = null
    this.ownerUserId = null
    try {
      await this.deps.clearDb()
      this.deps.storage.removeItem(CHAT_OWNER_KEY)
    } catch {
      // Best-effort.
    }
    this.setState({ conversations: [] })
  }

  private async persist(): Promise<void> {
    try {
      await this.deps.replaceConversations(this.state.conversations.map(conversationToRow))
    } catch {
      // Best-effort like mobile.
    }
  }
}
